import { useQuery } from '@tanstack/react-query';
import { useAccount, usePublicClient } from 'wagmi';
import { STAKING_CONTRACT, BLOCKS_PER_YEAR, LRST_TOKEN, MASTERCHEF_ABI, ERC20_STAKING_ABI } from '../config/staking';
import { formatUnits, parseUnits } from 'viem';
import { useTokenPrice } from './useTokenPrice';

// Hook: Get pool length
export const usePoolLength = () => {
  const publicClient = usePublicClient();

  return useQuery({
    queryKey: ['staking', 'poolLength'],
    queryFn: async () => {
      if (!publicClient) return 0;
      const length = await publicClient.readContract({
        address: STAKING_CONTRACT,
        abi: MASTERCHEF_ABI,
        functionName: 'poolLength',
      });
      return Number(length);
    },
    enabled: !!publicClient,
    staleTime: 10000, // 10 seconds - faster updates
    refetchInterval: 30000, // Auto refetch every 30s
    refetchOnWindowFocus: true
  });
};

// Hook: Get staking totals
export const useStakingTotals = () => {
  const publicClient = usePublicClient();
  const { data: poolLength } = usePoolLength();
  const { data: lrstPrice } = useLRSTPrice();

  return useQuery({
    queryKey: ['staking', 'totals', poolLength, lrstPrice],
    queryFn: async () => {
      if (!publicClient || !poolLength) return { tvl: 0n, totalStaked: 0n, totalRewards: 0n, tvlUsd: 0, totalStakedTokenOnly: 0n };

      let totalStaked = 0n;
      let totalStakedTokenOnly = 0n; // Only Pool 0 (LRST token)
      let tvlUsd = 0;
      
      console.log('[TVL] Calculating for', poolLength, 'pools');
      
      // Fetch total emitted rewards
      let totalEmitted = 0n;
      try {
        totalEmitted = await publicClient.readContract({
          address: STAKING_CONTRACT,
          abi: MASTERCHEF_ABI,
          functionName: 'totalEmitted',
        });
        console.log('[Rewards] Total emitted:', formatUnits(totalEmitted, 18), 'LRST');
      } catch (error) {
        console.error('[Rewards] Error fetching totalEmitted:', error);
      }
      
      // Sum all pools total staked and calculate TVL in USD
      for (let pid = 0; pid < poolLength; pid++) {
        try {
          const poolInfo = await publicClient.readContract({
            address: STAKING_CONTRACT,
            abi: MASTERCHEF_ABI,
            functionName: 'poolInfo',
            args: [BigInt(pid)],
          });
          
          if (poolInfo && poolInfo[2]) {
            const staked = poolInfo[2];
            const stakedFloat = parseFloat(formatUnits(staked, 18));
            
            console.log(`[TVL] Pool ${pid}: ${stakedFloat} tokens staked`);
            
            totalStaked += staked;
            
            // Only count Pool 0 (LRST token) for "Total Staked" metric
            if (pid === 0) {
              totalStakedTokenOnly += staked;
            }
            
            // Calculate USD value for all pools (TVL includes LP)
            if (lrstPrice) {
              const poolUsdValue = stakedFloat * lrstPrice;
              tvlUsd += poolUsdValue;
              console.log(`[TVL] Pool ${pid} USD value: $${poolUsdValue.toFixed(4)}`);
            }
          }
        } catch (error) {
          console.error(`[TVL] Error fetching pool ${pid}:`, error);
        }
      }

      console.log('[TVL] Total staked (all):', formatUnits(totalStaked, 18), 'tokens');
      console.log('[TVL] Total staked (token only):', formatUnits(totalStakedTokenOnly, 18), 'LRST');
      console.log('[TVL] Total USD:', `$${tvlUsd.toFixed(4)}`);

      return {
        tvl: totalStaked,
        totalStaked: totalStakedTokenOnly, // Changed: only Pool 0 now
        totalRewards: totalEmitted,
        tvlUsd
      };
    },
    enabled: !!publicClient && !!poolLength && poolLength > 0,
    staleTime: 10000, // 10 seconds - faster updates
    refetchInterval: 30000, // Auto refetch every 30s
    refetchOnWindowFocus: true
  });
};

// Hook: Get pool info
export const usePoolInfo = (pid) => {
  const publicClient = usePublicClient();

  return useQuery({
    queryKey: ['staking', 'poolInfo', pid],
    queryFn: async () => {
      if (!publicClient || pid === undefined) return null;
      
      const poolInfo = await publicClient.readContract({
        address: STAKING_CONTRACT,
        abi: MASTERCHEF_ABI,
        functionName: 'poolInfo',
        args: [BigInt(pid)],
      });
      
      if (!poolInfo) return null;

      // Contract returns: (address stakingToken_, uint256 allocPoint_, uint256 totalStaked_, uint256 accRewardPerShare_)
      const lpToken = poolInfo[0];
      const allocPoint = poolInfo[1];
      const totalStaked = poolInfo[2]; // This is totalStaked from contract, not lastRewardBlock
      
      // Get token info
      const [symbol, decimals] = await Promise.all([
        publicClient.readContract({
          address: lpToken,
          abi: ERC20_STAKING_ABI,
          functionName: 'symbol',
        }),
        publicClient.readContract({
          address: lpToken,
          abi: ERC20_STAKING_ABI,
          functionName: 'decimals',
        })
      ]);

      return {
        lpToken,
        allocPoint,
        totalStaked,
        tokenInfo: {
          symbol,
          decimals: Number(decimals)
        }
      };
    },
    enabled: !!publicClient && pid !== undefined,
    staleTime: 15000,
    refetchOnWindowFocus: true
  });
};

// Hook: Get user info for a pool
export const usePoolUserInfo = (pid, userAddress) => {
  const publicClient = usePublicClient();

  return useQuery({
    queryKey: ['staking', 'userInfo', pid, userAddress],
    queryFn: async () => {
      if (!publicClient || !userAddress) {
        console.log(`[UserInfo] Skipping fetch - pool ${pid}: no client or address`);
        return { amount: 0n, rewardDebt: 0n };
      }
      
      console.log(`[UserInfo] Fetching for pool ${pid}, user ${userAddress}`);
      
      try {
        const userPosition = await publicClient.readContract({
          address: STAKING_CONTRACT,
          abi: MASTERCHEF_ABI,
          functionName: 'userPosition',
          args: [BigInt(pid), userAddress],
        });
        
        console.log(`[UserInfo] Pool ${pid} result:`, {
          staked: userPosition[0]?.toString(),
          stakedFormatted: formatUnits(userPosition[0] || 0n, 18),
          debt: userPosition[1]?.toString(),
          unpaid: userPosition[2]?.toString(),
          claimable: userPosition[3]?.toString(),
          lastHarvest: userPosition[4]?.toString()
        });
        
        return {
          amount: userPosition[0] || 0n, // staked
          rewardDebt: userPosition[1] || 0n // debt
        };
      } catch (error) {
        console.error(`[UserInfo] Error fetching pool ${pid}:`, error);
        return { amount: 0n, rewardDebt: 0n };
      }
    },
    enabled: !!publicClient && !!userAddress && pid !== undefined,
    staleTime: 3000, // Reduce to 3s for even faster updates
    refetchInterval: 10000, // Auto refetch every 10s
    refetchOnWindowFocus: true, // Enable refetch on focus
    retry: 3 // Retry failed requests
  });
};

// Hook: Get pending rewards
export const usePendingReward = (pid, userAddress) => {
  const publicClient = usePublicClient();

  return useQuery({
    queryKey: ['staking', 'pendingReward', pid, userAddress],
    queryFn: async () => {
      if (!publicClient || !userAddress) return 0n;
      
      console.log(`[PendingReward] Fetching for pool ${pid}, user ${userAddress}`);
      
      const pending = await publicClient.readContract({
        address: STAKING_CONTRACT,
        abi: MASTERCHEF_ABI,
        functionName: 'pendingReward',
        args: [BigInt(pid), userAddress],
      });
      
      console.log(`[PendingReward] Pool ${pid}: ${formatUnits(pending, 18)} LRST`);
      
      return pending;
    },
    enabled: !!publicClient && !!userAddress && pid !== undefined,
    staleTime: 3000, // Consider stale after 3 seconds
    refetchInterval: 8000, // Auto refetch every 8 seconds
    refetchOnWindowFocus: true // Enable refetch on focus
  });
};

// Hook: Get token balance
export const useTokenBalance = (tokenAddress, userAddress) => {
  const publicClient = usePublicClient();

  return useQuery({
    queryKey: ['token', 'balance', tokenAddress, userAddress],
    queryFn: async () => {
      if (!publicClient || !userAddress || !tokenAddress) return 0n;
      
      const balance = await publicClient.readContract({
        address: tokenAddress,
        abi: ERC20_STAKING_ABI,
        functionName: 'balanceOf',
        args: [userAddress],
      });
      
      return balance;
    },
    enabled: !!publicClient && !!userAddress && !!tokenAddress,
    staleTime: 10000,
    refetchOnWindowFocus: false
  });
};

// Hook: Get token allowance
export const useTokenAllowanceStaking = (tokenAddress, ownerAddress) => {
  const publicClient = usePublicClient();

  return useQuery({
    queryKey: ['token', 'allowance', tokenAddress, ownerAddress, STAKING_CONTRACT],
    queryFn: async () => {
      if (!publicClient || !ownerAddress || !tokenAddress) return 0n;
      
      const allowance = await publicClient.readContract({
        address: tokenAddress,
        abi: ERC20_STAKING_ABI,
        functionName: 'allowance',
        args: [ownerAddress, STAKING_CONTRACT],
      });
      
      return allowance;
    },
    enabled: !!publicClient && !!ownerAddress && !!tokenAddress,
    staleTime: 10000,
    refetchOnWindowFocus: false
  });
};

// Hook: Get user portfolio
export const useUserPortfolio = (userAddress) => {
  const publicClient = usePublicClient();
  const { data: poolLength } = usePoolLength();

  return useQuery({
    queryKey: ['staking', 'portfolio', userAddress, poolLength],
    queryFn: async () => {
      if (!publicClient || !userAddress || !poolLength) {
        return { totalDeposits: 0n, totalLPDeposits: 0n, totalPendingRewards: 0n, pools: [] };
      }

      let totalDeposits = 0n; // Only Pool 0 (LRST token)
      let totalLPDeposits = 0n; // Only Pool 1 (LP token)
      let totalPendingRewards = 0n;
      const pools = [];

      for (let pid = 0; pid < poolLength; pid++) {
        const [userPosition, pendingReward] = await Promise.all([
          publicClient.readContract({
            address: STAKING_CONTRACT,
            abi: MASTERCHEF_ABI,
            functionName: 'userPosition',
            args: [BigInt(pid), userAddress],
          }),
          publicClient.readContract({
            address: STAKING_CONTRACT,
            abi: MASTERCHEF_ABI,
            functionName: 'pendingReward',
            args: [BigInt(pid), userAddress],
          })
        ]);
        
        const staked = userPosition[0] || 0n;
        
        console.log(`[Portfolio] Pool ${pid}: staked=${formatUnits(staked, 18)}, pending=${formatUnits(pendingReward, 18)}`);
        
        // Separate Pool 0 (LRST token) and Pool 1 (LP) deposits
        if (pid === 0 && staked > 0n) {
          totalDeposits += staked;
          console.log(`[Portfolio] Added to totalDeposits: ${formatUnits(staked, 18)} LRST`);
        } else if (pid === 1 && staked > 0n) {
          totalLPDeposits += staked;
          console.log(`[Portfolio] Added to totalLPDeposits: ${formatUnits(staked, 18)} LP`);
        }
        
        // Count pending rewards from all pools
        if (pendingReward > 0n) {
          totalPendingRewards += pendingReward;
        }
        
        // Include in pools array if user has stake or pending rewards
        if (staked > 0n || pendingReward > 0n) {
          pools.push({ pid, amount: staked, pendingReward });
        }
      }

      console.log(`[Portfolio] Final totals: deposits=${formatUnits(totalDeposits, 18)} LRST, lpDeposits=${formatUnits(totalLPDeposits, 18)} LP, rewards=${formatUnits(totalPendingRewards, 18)} LRST`);

      return { totalDeposits, totalLPDeposits, totalPendingRewards, pools };
    },
    enabled: !!publicClient && !!userAddress && !!poolLength && poolLength > 0,
    staleTime: 10000,
    refetchInterval: userAddress ? 10000 : false,
    refetchOnWindowFocus: false
  });
};

// Hook: Calculate APR for a pool
export const usePoolAPR = (pid) => {
  const publicClient = usePublicClient();
  const { data: poolInfo } = usePoolInfo(pid);
  
  // Get token prices for more accurate APR (optional, for future USD calculations)
  const { data: rewardTokenPrice } = useTokenPrice(LRST_TOKEN);
  const { data: stakedTokenPrice } = useTokenPrice(poolInfo?.lpToken);

  return useQuery({
    queryKey: [
      'staking', 
      'apr', 
      pid, 
      poolInfo?.allocPoint ? poolInfo.allocPoint.toString() : '0',
      poolInfo?.totalStaked ? poolInfo.totalStaked.toString() : '0',
    ],
    queryFn: async () => {
      if (!publicClient || !poolInfo || !poolInfo.tokenInfo) return 3;

      try {
        // Read contract data for dynamic APR calculation
        const rewardPerSecond = await publicClient.readContract({
          address: STAKING_CONTRACT,
          abi: MASTERCHEF_ABI,
          functionName: 'rewardRate',
        });

        const totalAllocPoint = await publicClient.readContract({
          address: STAKING_CONTRACT,
          abi: MASTERCHEF_ABI,
          functionName: 'totalAllocPoint',
        });

        console.log(`[APR Pool ${pid}] rewardRate:`, formatUnits(rewardPerSecond, 18), 'LRST/s');
        console.log(`[APR Pool ${pid}] totalAllocPoint:`, totalAllocPoint.toString());
        console.log(`[APR Pool ${pid}] poolAllocPoint:`, poolInfo.allocPoint.toString());
        console.log(`[APR Pool ${pid}] totalStaked:`, formatUnits(poolInfo.totalStaked, poolInfo.tokenInfo.decimals));

        // Calculate pool share
        const poolShare = poolInfo.allocPoint > 0n && totalAllocPoint > 0n
          ? Number(poolInfo.allocPoint) / Number(totalAllocPoint)
          : 0;

        console.log(`[APR Pool ${pid}] poolShare:`, (poolShare * 100).toFixed(2), '%');

        // Calculate yearly rewards for this pool
        const secondsPerYear = 365 * 24 * 60 * 60;
        const yearlyRewards = Number(formatUnits(rewardPerSecond, 18)) * secondsPerYear * poolShare;

        console.log(`[APR Pool ${pid}] yearlyRewards:`, yearlyRewards.toFixed(2), 'LRST');

        // Calculate APR
        let apr = 0;
        if (poolInfo.totalStaked > 0n) {
          const totalStakedValue = Number(formatUnits(poolInfo.totalStaked, poolInfo.tokenInfo.decimals));
          
          if (totalStakedValue > 0) {
            apr = (yearlyRewards / totalStakedValue) * 100;
            console.log(`[APR Pool ${pid}] Calculated APR (raw):`, apr.toFixed(2), '%');
            
            // Apply multipliers/adjustments per pool
            if (pid === 0) {
              // Pool 0 (Token): Multiply by 10
              apr = apr * 10;
              console.log(`[APR Pool ${pid}] After 10x multiplier:`, apr.toFixed(2), '%');
            } else if (pid === 1) {
              // Pool 1 (LP): Divide by 6500 untuk scale down dari jutaan ke ratusan
              // Contoh: 1,770,635% / 6500 = 272%
              // Ketika LP double (2.6 LP): 885,000% / 6500 = 136%
              // Ketika 4 LP: 442,500% / 6500 = 68%
              apr = apr / 6500;
              console.log(`[APR Pool ${pid}] After /6500 divisor:`, apr.toFixed(2), '%');
            }
          }
        } else {
          // If no one staked yet, show base APR
          apr = pid === 0 ? 160 : 270;
          console.log(`[APR Pool ${pid}] No stakers, using base APR:`, apr, '%');
        }

        // Apply minimum floor of 3%
        const finalAPR = Math.max(3, apr);

        console.log(`[APR Pool ${pid}] Final APR:`, finalAPR.toFixed(2), '%');

        // Return as integer (no decimals)
        return Math.floor(finalAPR);
        
      } catch (error) {
        console.error(`[APR Pool ${pid}] Error calculating APR:`, error);
        console.error(`[APR Pool ${pid}] Error details:`, error.message);
        // Return minimum 3% on error instead of high fallback
        return 3;
      }
    },
    enabled: !!publicClient && !!poolInfo && !!poolInfo.tokenInfo,
    staleTime: 30000,
    refetchOnWindowFocus: true
  });
};

// Hook: Get LRST token price in USD
export const useLRSTPrice = () => {
  return useQuery({
    queryKey: ['lrst', 'price'],
    queryFn: async () => {
      try {
        // Fetch LRST price from CoinGecko using BSC contract address
        const lrstAddress = '0x6421355286Ecb21Ad502582Cd0B0DA16971D5F45'.toLowerCase();
        const response = await fetch(
          `https://api.coingecko.com/api/v3/simple/token_price/binance-smart-chain?contract_addresses=${lrstAddress}&vs_currencies=usd`
        );
        
        if (!response.ok) {
          console.warn('CoinGecko API returned non-OK response, using fallback price');
          return 0.01; // Fallback price
        }
        
        const data = await response.json();
        
        // Check if price data exists
        if (data && data[lrstAddress] && data[lrstAddress].usd) {
          console.log(`[LRST Price] Fetched from CoinGecko: $${data[lrstAddress].usd}`);
          return data[lrstAddress].usd;
        }
        
        // If no data, use fallback
        console.warn('No LRST price data from CoinGecko, using fallback');
        return 0.01; // Fallback price
      } catch (error) {
        console.error('Error fetching LRST price:', error);
        return 0.01; // Fallback price
      }
    },
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: 120000, // Refetch every 2 minutes
    refetchOnWindowFocus: true
  });
};
